import axios from 'axios';
import { useEffect, useState } from 'react';
import TestBreakdownTable from './tables/TestBreakdownTable';
import TableHeader from '../../components/generic/TableHeader';
import { Tab, TabsContainer } from '../../utils/SubjectTabs';
import ComparativeTestPerformanceTable from './tables/ComparativeTestPerformanceTable';
import ComparativeTestBreakdownTable from './tables/ComparativeTestBreakdownTable';
import TestAnalysisLoader from '../../components/loaders/TestAnalysisLoader';

export default function PerformanceAnalysis({ isComparative, comparativeData, totalMarks, packId, testId }) {
  const [performance, setPerformance] = useState(null);
  const [comparativeAccuracy, setComparativeAccuracy] = useState(null);
  const [comparativeScore, setComparativeScore] = useState(null);
  const [comparativeTestBreakdown, setComparativeTestBreakdown] = useState(null);
  const [userAccuracy, setUserAccuracy] = useState(null);
  const [userScore, setUserScore] = useState(null);
  const [userTestBreakdown, setUserTestBreakdown] = useState(null);
  const [subjectMeta, setSubjectMeta] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`/api/test/analysis/v2/${packId}/${testId}/performanceAnalysis`);
        const data = response?.data?.performance;
        const subjectMeta = response?.data?.subjectMeta;
        for (const subject in data.testPerformance) {
          if (data.testBreakdown[subject]) {
            data.testBreakdown[subject] = {
              ...data.testBreakdown[subject],
              ...data.testPerformance[subject],
            };
          }
        }
        const result = Object.entries(data.testBreakdown)
          .map(([subject, subjectData]) => ({
            subject,
            ...subjectData,
            ...subjectMeta[subject],
          }))
          .sort((a, b) => {
            if (a.subject === 'overall') return -1;
            if (b.subject === 'overall') return 1;
            return 0;
          });

        setPerformance(result);
      } catch (err) {
      } finally {
        setIsLoading(false);
      }
    };
    if (!isComparative && !performance) {
      fetchData();
    }
  }, [packId, testId]);

  useEffect(() => {
    if (!comparativeData || !isComparative) return undefined;
    setComparativeAccuracy(comparativeData?.comparativeData?.accuracy);
    setComparativeScore(comparativeData?.comparativeData?.scores);
    setComparativeTestBreakdown(comparativeData?.comparativeData?.test_breakdown);
    setUserAccuracy(comparativeData?.userData?.accuracy);
    setUserScore(comparativeData?.userData?.scores);
    setUserTestBreakdown(comparativeData?.userData?.test_breakdown);
    let subjectMeta = structuredClone(comparativeData?.subjectMeta);
    if (Object.keys(subjectMeta).length > 2) {
      const overall = { Overall: subjectMeta['Overall'] };
      delete subjectMeta['Overall'];
      subjectMeta = { ...overall, ...subjectMeta };
    } else {
      delete subjectMeta['Overall'];
    }
    setSubjectMeta(subjectMeta);
    setSelectedSubject(Object.values(subjectMeta)[0]);
  }, [isComparative, comparativeData]);

  const handleSubjectChange = (subject) => {
    setSelectedSubject(subject);
  };

  if (isLoading) return <TestAnalysisLoader />;

  if (isComparative && subjectMeta)
    return (
      <div className="space-y-8">
        <TableHeader headerTitle={'Test Performance'} headerTitleClass={'border-b-0'}>
          <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
            <TabsContainer color={selectedSubject?.color}>
              {Object.values(subjectMeta)?.map((tab) => (
                <Tab
                  key={tab?._id}
                  isActive={selectedSubject?.title === tab?.title}
                  color={selectedSubject?.title === tab?.title ? selectedSubject?.color : 'var(--q3-neutral-light)'}
                  tabColor={tab?.color}
                  bgColor="var(--q3-surface-dim)"
                  onClick={() => handleSubjectChange(tab)}
                >
                  {tab?.icon && <img src={tab?.icon} alt={tab?.title} className="" />}
                  <p className="mb-0">{tab?.title}</p>
                </Tab>
              ))}
            </TabsContainer>
          </div>
          <ComparativeTestPerformanceTable
            comparativeAccuracy={comparativeAccuracy}
            comparativeScore={comparativeScore}
            userAccuracy={userAccuracy}
            userScore={userScore}
            totalMarks={totalMarks}
            selectedSubject={selectedSubject}
          />
        </TableHeader>
        <TableHeader headerTitle={'Test Breakdown'} headerTitleClass={'border-b-0'}>
          <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
            <TabsContainer color={selectedSubject?.color}>
              {Object.values(subjectMeta)?.map((tab) => (
                <Tab
                  key={tab?._id}
                  isActive={selectedSubject?.title === tab?.title}
                  color={selectedSubject?.title === tab?.title ? selectedSubject?.color : 'var(--q3-neutral-light)'}
                  tabColor={tab?.color}
                  bgColor="var(--q3-surface-dim)"
                  onClick={() => handleSubjectChange(tab)}
                >
                  {tab?.icon && <img src={tab?.icon} alt={tab?.title} className="" />}
                  <p className="mb-0">{tab?.title}</p>
                </Tab>
              ))}
            </TabsContainer>
          </div>
          <ComparativeTestBreakdownTable
            comparativeTestBreakdown={comparativeTestBreakdown}
            userTestBreakdown={userTestBreakdown}
            totalQuestions={comparativeData?.totalQuestions}
            selectedSubject={selectedSubject}
          />
        </TableHeader>
      </div>
    );

  return (
    <TableHeader headerTitle={'Test Breakdown'}>
      <TestBreakdownTable performance={performance} />
    </TableHeader>
  );
}
